// Step 1: where the worker comes from.
//
// Three choices with very different consequences, so they are cards with real
// explanations rather than a dropdown: a local worker shares this machine's CPU
// and memory with the manager, which is fine for testing and small volumes and
// wrong for anything else, and "already running" creates nothing at all.

import { RadioGroup } from "@veil/ui/components/radio-group";
import ClipboardList from "lucide-react/dist/esm/icons/clipboard-list";
import MonitorSmartphone from "lucide-react/dist/esm/icons/monitor-smartphone";
import Server from "lucide-react/dist/esm/icons/server";
import { Controller, type UseFormReturn } from "react-hook-form";
import { ChoiceCard } from "@/components/form/choice-card";
import type { DeployTarget, DeployWorkerValue } from "../schema";

export function TargetStep({
	form,
}: {
	form: UseFormReturn<DeployWorkerValue>;
}) {
	return (
		<Controller
			control={form.control}
			name="target"
			render={({ field }) => (
				<RadioGroup
					value={field.value}
					onValueChange={(v) => field.onChange(v as DeployTarget)}
					aria-label="Where the worker comes from"
					className="gap-2"
				>
					<ChoiceCard
						id="deploy-target-local"
						value="local"
						icon={MonitorSmartphone}
						title="On this machine"
						description="Deployed here in one click. Shares this machine's CPU, memory and disk with the manager, so it suits testing and low volume rather than a production fleet."
					/>
					<ChoiceCard
						id="deploy-target-remote"
						value="remote"
						icon={Server}
						title="On another machine (SSH)"
						description="A dedicated host, so browser load never competes with the manager. Needs SSH access with root or passwordless sudo; Docker is installed for you if you ask it to be."
					/>
					<ChoiceCard
						id="deploy-target-register"
						value="register"
						icon={ClipboardList}
						title="Already running"
						description="Records a worker you deployed yourself and puts it in the pool. Nothing is created — this only tells the manager how to reach it."
					/>
				</RadioGroup>
			)}
		/>
	);
}
